import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Link as LinkIcon,
  Loader2,
  Upload,
} from "lucide-react";
import {
  ContractDetails,
  PartnershipTermsDialog,
} from "@/components/Partnership/PartnershipTermsDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatCurrency, formatDate } from "@/utils/format";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import ContractTab from "@/components/Partnership/FormalContract/ContractTab";
import { ContractTermsData } from "@/components/Partnership/ContractTermsForm";
import { ConvexError } from "convex/values";
import { ERRORS } from "~/errors";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OverviewTab from "@/components/Partnership/OverviewTab";
import { PartnershipDocumentUpload } from "@/components/Partnership/PartnershipDocumentUpload";
import { PartnershipFeedbackVideos } from "@/components/Partnership/VideoFeedback/PartnershipFeedbackVideos";
import { PartnershipPerformance } from "@/components/Partnership/PartnershipPerformance";
import { Route as PartnershipsRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.index";
import { PayoutCalculator } from "@/components/Partnership/PayoutCalculator";
import StatusBadge from "@/components/Generic/StatusBadge";
import { Route as WelcomeRoute } from "@/routes/_app/_auth/dashboard/_layout.welcome";
import { api } from "@cvx/_generated/api";
import { calculateEarningsForContract } from "@cvx/helpers";
import { cn } from "@/utils/misc";
import siteConfig from "~/site.config";
import { useAccount } from "@/state/useAccount";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

export const Route = createFileRoute(
  "/_app/_auth/partnerships/$partnershipId/",
)({
  component: PartnershipDetails,
  validateSearch: (search: Record<string, unknown>) => {
    // URL search params support:
    // - tab: "overview" | "performance" | "videos" (sets active tab)
    // - projectId: string (opens specific video project when tab=videos)
    // - versionId: string (opens specific version when projectId is also specified)
    // - openChat: boolean (automatically opens the chat on page load)
    //
    // Examples:
    // - ?tab=videos - opens videos tab
    // - ?tab=videos&projectId=123 - opens videos tab with project 123 selected
    // - ?tab=videos&projectId=123&versionId=456 - opens project 123 with version 456
    // - ?openChat=true - opens the chat automatically
    return {
      tab: (search.tab as string) || undefined,
      projectId: (search.projectId as string) || undefined,
      versionId: (search.versionId as string) || undefined,
      openChat: search.openChat === true || search.openChat === "true",
    };
  },
  //TODO: make sure you can't access this page if you're not the creator or one of the founder
  beforeLoad: () => ({
    title: `${siteConfig.siteTitle} - Partnership Details`,
    headerTitle: "Partnership Details",
    headerDescription: "View and manage partnership details.",
  }),
});

// Update ContractDetails interface to match new structure
export interface NewContractDetails extends ContractTermsData { }

function PartnershipDetails() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { partnershipId } = Route.useParams();
  const { toast } = useToast();

  // Initialize activeTab from URL search params
  const [activeTab, setActiveTab] = useState(search.tab || "overview");
  const [showContractForm, setShowContractForm] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [_, setIsChatOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showExtendContractDialog, setShowExtendContractDialog] =
    useState(false);
  const [showInvoiceUploadDialog, setShowInvoiceUploadDialog] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [invoiceMode, setInvoiceMode] = useState<"file" | "url">("file");
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [newEndDate, setNewEndDate] = useState<Date | undefined>(undefined);
  const [contractDetails, setContractDetails] = useState<ContractDetails>({
    startDate: Date.now(),
    endDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ).getTime(), // End of current month
    currency: "usd",
    requiredVideos: {
      count: 1,
      duration: 30, // 30 days default
    },
    flatFee: {
      perVideo: 0,
    },
    cpmRate: {
      type: "perVideo",
      threshold: 0,
      rate: 0,
      cutoffPeriod: 10, // Default cutoff period
    },
  });

  const { appId, creatorId } = useAccount();

  // Update activeTab when search params change
  useEffect(() => {
    if (search.tab && search.tab !== activeTab) {
      setActiveTab(search.tab);
    }
  }, [search.tab]);

  // Open chat automatically if openChat parameter is true
  useEffect(() => {
    if (search.openChat) {
      setIsChatOpen(true);
    }
  }, [search.openChat]);

  // Update URL when activeTab changes
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    navigate({
      to: ".",
      search: (prev: {
        tab?: string;
        projectId?: string;
        versionId?: string;
      }) => ({
        ...prev,
        tab: newTab,
        // Clear project/version params when switching away from videos tab
        ...(newTab !== "videos"
          ? { projectId: undefined, versionId: undefined }
          : {}),
      }),
      replace: true,
    });
  };

  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  const { mutateAsync: terminatePartnership } = useMutation({
    mutationFn: useConvexMutation(api.partnerships.terminatePartnership),
  });

  const {
    mutateAsync: createUpdatePartnership,
    isPending: isCreatingPartnership,
  } = useMutation({
    mutationFn: useConvexMutation(api.partnerships.createUpdatePartnership),
  });

  const { mutateAsync: approvePartnership } = useMutation({
    mutationFn: useConvexMutation(api.partnerships.approvePartnership),
  });

  const { data: partnership, isLoading: isLoadingPartnership } = useQuery(
    convexQuery(api.partnerships.getPartnership, {
      partnershipId: partnershipId as Id<"creatorContracts">,
    }),
  );

  //   const { data: creator } = useQuery(
  //     convexQuery(api.creators.getCreatorByCreatorId, {
  //       creatorId: partnership?.partnership.creatorId,
  //     }),
  //   );

  // Fetch contract videos for earnings calculation
  const { data: contractVideos } = useQuery(
    convexQuery(api.videos.getCreatorPublishedVideosByContract, {
      contractId: partnershipId as Id<"creatorContracts">,
    }),
  );

  const { mutateAsync: removeDocument } = useMutation({
    mutationFn: useConvexMutation(
      api.partnerships.removeDocumentFromPartnership,
    ),
  });

  const { mutateAsync: createContractRevision, isPending: isCreatingRevision } =
    useMutation({
      mutationFn: useConvexMutation(
        api.contractRevisions.createContractRevision,
      ),
    });

  const {
    mutateAsync: approveRejectRevision,
    isPending: isApprovingRejecting,
  } = useMutation({
    mutationFn: useConvexMutation(
      api.contractRevisions.approveRejectContractRevision,
    ),
  });

  const { data: contractRevisions } = useQuery(
    convexQuery(api.contractRevisions.getContractRevisions, {
      contractId: partnershipId as Id<"creatorContracts">,
    }),
  );

  const { mutateAsync: uploadCreatorInvoice } = useMutation({
    mutationFn: useConvexMutation(api.payouts.uploadCreatorInvoice),
  });

  // File upload functionality for invoice
  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);
  const { startUpload: startInvoiceUpload } = useUploadFiles(
    generateUploadUrl,
    {
      onUploadComplete: async (uploaded) => {
        try {
          const uploadResponse = uploaded[0].response as {
            storageId: Id<"_storage">;
          };

          await uploadCreatorInvoice({
            contractId: partnershipId as Id<"creatorContracts">,
            invoice: uploadResponse.storageId,
          });

          toast({
            title: "Invoice uploaded successfully",
            description:
              "The app founder will be notified and can now process your payment.",
          });

          setShowInvoiceUploadDialog(false);
          setInvoiceFile(null);
          setInvoiceUrl("");
        } catch (error) {
          console.error("Error uploading invoice:", error);
          toast({
            title: "Error uploading invoice",
            description:
              error instanceof ConvexError
                ? error.data
                : "There was an error uploading your invoice",
            variant: "destructive",
          });
        } finally {
          setIsUploadingInvoice(false);
        }
      },
      onUploadError: () => {
        toast({
          title: "Error uploading invoice",
          description: "There was an error uploading your invoice",
          variant: "destructive",
        });
        setIsUploadingInvoice(false);
      },
    },
  );

  const contract = useMemo(() => {
    if (!partnership) return null;

    // Priority: use new fields first, fallback to legacy fields
    const flatFeePerVideo = partnership.partnership.flatFee?.perVideo ?? 0;
    const cpm = partnership.partnership.cpmRate?.rate ?? 0;
    const minimumViewsRequired =
      partnership.partnership.cpmRate?.threshold ?? 0;
    const maximumPaymentCap = partnership.partnership.cpmRate?.maxPayout ?? 0;
    const requiredVideoCount =
      partnership.partnership.requiredVideos?.count ?? 0;

    return {
      status: partnership.partnership.status,
      contractTerms: {
        flatFeePerVideo,
        cpm,
        minimumViewsRequiredPerVideo: minimumViewsRequired,
        maximumPaymentCapPerVideo: maximumPaymentCap,
        currency: partnership.partnership.currency || "usd",
      },
      deliverables: {
        requiredVideoCount,
      },
      documents: partnership.partnership.documents,
      timeline: [
        {
          date: new Date(partnership.partnership.startDate).toISOString(),
          event: "Contract Start",
          status: "completed" as const,
        },
        {
          date: new Date(partnership.partnership.endDate).toISOString(),
          event: "Contract End",
          status:
            new Date(partnership.partnership.endDate) < new Date()
              ? ("completed" as const)
              : ("upcoming" as const),
        },
      ],
      founderContact: {
        name: "CreatorGrid Support Team",
        email: "creators@creatorgrid.io",
        phone: undefined,
      },
      creator: {
        id: partnership.creator.creatorId,
        name: partnership.creator.name,
        profilePicture: partnership.creator.image,
      },
      rejectionReason: undefined,
      campaign: partnership.campaign,
    };
  }, [partnership]);

  useEffect(() => {
    if (partnership) {
      setContractDetails({
        startDate: partnership.partnership.startDate,
        endDate: partnership.partnership.endDate,
        currency: partnership.partnership.currency,
        requiredVideos: partnership.partnership.requiredVideos,
        flatFee: partnership.partnership.flatFee,
        cpmRate: partnership.partnership.cpmRate,
      });
    }
  }, [partnership]);

  // Calculate earnings if contract is completed or terminated
  const paymentInfo = useMemo(() => {
    if (!partnership || !contractVideos) return null;

    const status = partnership.partnership.status;
    if (status !== "completed" && status !== "terminated") return null;

    const totalEarnings = calculateEarningsForContract(
      partnership.partnership,
      contractVideos,
    );

    const totalAmount =
      totalEarnings.earningsFlatFee +
      totalEarnings.earningsPerVideoBonus +
      totalEarnings.earningsCPM;

    // Calculate if we're within the CPM cutoff period
    let isWithinCutoffPeriod = false;
    let cutoffDate: Date | undefined;
    let daysUntilCutoff: number | undefined;
    let cutoffPeriod: number | undefined;

    if (partnership.partnership.cpmRate?.cutoffPeriod) {
      cutoffPeriod = partnership.partnership.cpmRate.cutoffPeriod;
      const contractEndDate = new Date(partnership.partnership.endDate);
      cutoffDate = new Date(
        contractEndDate.getTime() + cutoffPeriod * 24 * 60 * 60 * 1000,
      );

      const now = new Date();
      isWithinCutoffPeriod = now < cutoffDate;

      if (isWithinCutoffPeriod) {
        daysUntilCutoff = Math.ceil(
          (cutoffDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
        );
      }
    }

    return {
      totalEarnings: totalAmount,
      breakdown: totalEarnings,
      paidAmount: partnership.partnership.paidAmount,
      paymentDate: partnership.partnership.paymentDate,
      paymentReceipt: partnership.partnership.paymentReceipt,
      isPaid:
        partnership.partnership.paidAmount &&
        partnership.partnership.paymentDate,
      amountOwed: totalAmount - (partnership.partnership.paidAmount || 0),
      isWithinCutoffPeriod,
      cutoffDate,
      daysUntilCutoff,
      cutoffPeriod,
    };
  }, [partnership, contractVideos]);

  // Handle URL navigation for videos
  const handleVideoNavigation = (projectId?: string, versionId?: string) => {
    navigate({
      to: ".",
      search: (prev: {
        tab?: string;
        projectId?: string;
        versionId?: string;
      }) => ({
        ...prev,
        tab: "videos",
        projectId: projectId || undefined,
        versionId: versionId || undefined,
      }),
      replace: true,
    });
  };

  const handleRemoveDocument = async (documentName: string) => {
    try {
      await removeDocument({
        partnershipId: partnershipId as Id<"creatorContracts">,
        documentName,
      });
      toast({
        title: "Document removed",
        description: "The document has been removed successfully",
      });
    } catch (error) {
      toast({
        title: "Error removing document",
        description: "There was an error removing the document",
        variant: "destructive",
      });
    }
  };

  const handleAcceptTerms = async () => {
    try {
      await approvePartnership({
        partnershipId: partnershipId as Id<"creatorContracts">,
      });
      toast({
        title: "Congratulations!",
        description:
          "The partnership has been approved. You can now start creating content for the app.",
      });
      setShowConfirmDialog(false);

      // Navigate to welcome screen
      if (partnership?.campaign.type === "challenge") {
        navigate({
          to: WelcomeRoute.fullPath,
          search: { appName: partnership.app.name },
          replace: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error approving partnership",
        description:
          error instanceof ConvexError
            ? error.data
            : ERRORS.SOMETHING_WENT_WRONG,
        variant: "destructive",
      });
    }
  };

  const handleExtendContract = async () => {
    if (!newEndDate) {
      toast({
        title: "Please select a new end date",
        variant: "destructive",
      });
      return;
    }

    try {
      await createContractRevision({
        contractId: partnershipId as Id<"creatorContracts">,
        newEndDate: newEndDate.getTime(),
      });

      toast({
        title: "Contract extension requested",
        description: "The creator will need to approve the extension.",
      });
      setShowExtendContractDialog(false);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error extending contract",
        description:
          error instanceof ConvexError
            ? error.data
            : ERRORS.SOMETHING_WENT_WRONG,
        variant: "destructive",
      });
    }
  };

  const handleApproveReject = async (
    revisionId: Id<"creatorContractRevisions">,
    status: "approved" | "rejected",
  ) => {
    try {
      await approveRejectRevision({
        revisionId,
        status,
      });

      toast({
        title: `Revision ${status}`,
        description:
          status === "approved"
            ? "The contract has been extended."
            : "The revision has been rejected.",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: `Error ${status === "approved" ? "approving" : "rejecting"} revision`,
        description:
          error instanceof ConvexError
            ? error.data
            : ERRORS.SOMETHING_WENT_WRONG,
        variant: "destructive",
      });
    }
  };

  const formatRevisionDate = (timestamp: number | undefined) => {
    if (!timestamp) return "Unknown";
    return formatDate(new Date(timestamp).toISOString());
  };

  const validateInvoiceUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    return url.toLowerCase().startsWith("https://");
  };

  const handleUrlChange = (url: string) => {
    setInvoiceUrl(url);

    if (url.trim() && !validateInvoiceUrl(url)) {
      setUrlError("URL must start with https://");
    } else {
      setUrlError("");
    }
  };

  const handleInvoiceUpload = async () => {
    if (invoiceMode === "file" && !invoiceFile) {
      toast({
        title: "No file selected",
        description: "Please select an invoice file to upload",
        variant: "destructive",
      });
      return;
    }

    if (invoiceMode === "url" && !invoiceUrl.trim()) {
      toast({
        title: "Missing invoice URL",
        description: "Please provide an invoice URL",
        variant: "destructive",
      });
      return;
    }

    if (invoiceMode === "url" && !validateInvoiceUrl(invoiceUrl)) {
      toast({
        title: "Invalid URL",
        description: "Invoice URL must start with https://",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingInvoice(true);

    try {
      if (invoiceMode === "file" && invoiceFile) {
        // Handle file upload through useUploadFiles
        startInvoiceUpload([invoiceFile]);
      } else if (invoiceMode === "url") {
        // Handle URL submission directly
        await uploadCreatorInvoice({
          contractId: partnershipId as Id<"creatorContracts">,
          invoice: invoiceUrl,
        });

        toast({
          title: "Invoice uploaded successfully",
          description:
            "The app founder will be notified and can now process your payment.",
        });

        setShowInvoiceUploadDialog(false);
        setInvoiceFile(null);
        setInvoiceUrl("");
        setUrlError("");
        setIsUploadingInvoice(false);
      }
    } catch (error) {
      console.error("Error uploading invoice:", error);
      toast({
        title: "Error uploading invoice",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error uploading your invoice",
        variant: "destructive",
      });
      setIsUploadingInvoice(false);
    }
  };

  const closeInvoiceModal = () => {
    setShowInvoiceUploadDialog(false);
    setInvoiceFile(null);
    setInvoiceUrl("");
    setUrlError("");
    setInvoiceMode("file");
  };

  if (isLoadingPartnership) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary/60" />
      </div>
    );
  }

  if (!partnership || !contract) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <div className="text-lg text-primary/60">Partnership not found</div>
      </div>
    );
  }

  const renderMainContent = () => {
    if (contract.status === "pendingApproval") {
      return (
        <Card className="col-span-2">
          <div className="flex flex-col items-center justify-center p-12 space-y-6">
            <h2 className="text-lg font-medium text-primary text-center">
              Partnership Pending
            </h2>
            <p className="mt-2 text-center text-sm text-primary/60 max-w-md">
              {appId
                ? "Waiting for the creator to approve the partnership terms. You can still edit the terms while waiting for approval."
                : "Please review the partnership terms below. You can accept them or request changes."}
            </p>
            <div className="rounded-lg bg-secondary p-6 w-full max-w-lg space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {/* Flat Fee Per Video */}
                {(contractDetails.flatFee?.perVideo ||
                  contract.contractTerms.flatFeePerVideo > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        Flat Fee Per Video
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(
                          contractDetails.flatFee?.perVideo ||
                          contract.contractTerms.flatFeePerVideo,
                        )}
                      </p>
                    </div>
                  )}

                {/* Time Period Fee (Monthly/Recurring) */}
                {contractDetails.flatFee?.timePeriod && (
                  <div>
                    <p className="text-sm text-primary/60">
                      {Math.abs(
                        (contractDetails.endDate - contractDetails.startDate) /
                        (1000 * 60 * 60 * 24) -
                        contractDetails.flatFee.timePeriod.days,
                      ) <= 1
                        ? "One Time Payment"
                        : `Flat Fee per ${contractDetails.flatFee.timePeriod.days} days`}
                    </p>
                    <p className="text-sm font-medium text-primary">
                      {formatCurrency(
                        contractDetails.flatFee.timePeriod.amount,
                      )}
                    </p>
                  </div>
                )}

                {/* CPM Rate */}
                {(contractDetails.cpmRate ||
                  contract.contractTerms.cpm > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        CPM (Cost Per Thousand Views)
                        {contractDetails.cpmRate?.type === "totalViews" &&
                          " - Total Views"}
                        {contractDetails.cpmRate?.type === "perVideo" &&
                          " - Per Video"}
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(
                          contractDetails.cpmRate?.rate ||
                          contract.contractTerms.cpm,
                          3,
                        )}
                      </p>
                    </div>
                  )}

                {/* Minimum Views Threshold */}
                {(contractDetails.cpmRate?.threshold ||
                  contract.contractTerms.minimumViewsRequiredPerVideo > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        Minimum Views Required
                        {contractDetails.cpmRate?.type === "totalViews" &&
                          " (Total)"}
                        {contractDetails.cpmRate?.type === "perVideo" &&
                          " (Per Video)"}
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {(
                          contractDetails.cpmRate?.threshold ||
                          contract.contractTerms.minimumViewsRequiredPerVideo
                        ).toLocaleString()}{" "}
                        views
                      </p>
                    </div>
                  )}

                {/* Maximum Payment Cap */}
                {(contractDetails.cpmRate?.maxPayout ||
                  contract.contractTerms.maximumPaymentCapPerVideo > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        Maximum Payment Cap
                        {contractDetails.cpmRate?.type === "totalViews" &&
                          " (Total)"}
                        {contractDetails.cpmRate?.type === "perVideo" &&
                          " (Per Video)"}
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(
                          contractDetails.cpmRate?.maxPayout ||
                          contract.contractTerms.maximumPaymentCapPerVideo,
                        )}
                      </p>
                    </div>
                  )}

                {/* CPM Cutoff Period */}
                {contractDetails.cpmRate?.cutoffPeriod && (
                  <div>
                    <p className="text-sm text-primary/60">
                      CPM Payment Cutoff Period
                    </p>
                    <p className="text-sm font-medium text-primary">
                      {contractDetails.cpmRate.cutoffPeriod} days after contract
                      ends
                    </p>
                  </div>
                )}

                {/* Required Videos */}
                {contractDetails.requiredVideos.count > 0 && (
                  <div>
                    <p className="text-sm text-primary/60">Required Videos</p>
                    <p className="text-sm font-medium text-primary">
                      {contractDetails.requiredVideos.count} video
                      {contractDetails.requiredVideos.count !== 1 ? "s" : ""}
                      {contractDetails.requiredVideos.duration &&
                        Math.abs(
                          (contractDetails.endDate - contractDetails.startDate) /
                          (1000 * 60 * 60 * 24) -
                          contractDetails.requiredVideos.duration,
                        ) <= 1
                        ? " for the duration of the contract"
                        : ` every ${contractDetails.requiredVideos.duration} days`}
                    </p>
                    <p className="text-xs text-primary/60 italic mt-1">
                      Note: This is the total number of videos you need to
                      publish across all platforms.
                    </p>
                  </div>
                )}

                {/* Contract Duration */}
                <div>
                  <p className="text-sm text-primary/60">Contract Duration</p>
                  <p className="text-sm font-medium text-primary">
                    {formatDate(
                      new Date(contractDetails.startDate).toISOString(),
                    )}{" "}
                    -{" "}
                    {formatDate(
                      new Date(contractDetails.endDate).toISOString(),
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <PayoutCalculator
                  flatFeePerVideo={
                    contractDetails.flatFee?.perVideo ||
                    contract.contractTerms.flatFeePerVideo
                  }
                  cpm={
                    contractDetails.cpmRate?.rate || contract.contractTerms.cpm
                  }
                  maximumPaymentCap={
                    contractDetails.cpmRate?.maxPayout ||
                    contract.contractTerms.maximumPaymentCapPerVideo
                  }
                  flatFeeTimePeriod={contractDetails.flatFee?.timePeriod}
                  cpmRateType={contractDetails.cpmRate?.type || "perVideo"}
                  contractDurationDays={Math.ceil(
                    Math.abs(
                      (contractDetails.endDate - contractDetails.startDate) /
                      (1000 * 60 * 60 * 24),
                    ),
                  )}
                  requiredVideos={contractDetails.requiredVideos}
                />
              </div>

              {appId ? (
                <Button
                  className="w-full mt-4"
                  variant="outline"
                  onClick={() => setShowContractForm(true)}
                >
                  Edit Terms
                </Button>
              ) : (
                <div className="flex gap-3 mt-4">
                  <Button
                    className="flex-1"
                    onClick={() => setShowConfirmDialog(true)}
                  >
                    Accept Terms
                  </Button>
                  <Button
                    className="flex-1"
                    variant="outline"
                    onClick={() => {
                      setIsChatOpen(true);
                    }}
                  >
                    Request Changes
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      );
    }

    if (contract.status === "completed" || contract.status === "terminated") {
      return (
        <div className="col-span-2 space-y-6">
          <Card>
            <div className="flex flex-col justify-between p-6 gap-4">
              <div className="space-y-2">
                <h2 className="text-lg font-medium text-primary">
                  Partnership{" "}
                  {contract.status === "completed" ? "Ended" : "Terminated"}
                </h2>
                <p className="text-sm text-primary/60">
                  {contract.status === "completed"
                    ? "This partnership has ended. View your historical performance below or contact the app owner to renew."
                    : "This partnership has been terminated. View your historical performance below or contact the app owner to renew."}
                </p>
              </div>
              <Button variant="secondary" onClick={() => setIsChatOpen(true)}>
                Contact to Renew
              </Button>
            </div>
          </Card>

          {/* Show historical data for expired partnerships */}
          <Card className="divide-y divide-border">
            <div className="p-6">
              <h2 className="text-lg font-medium text-primary">
                Previous Contract Terms
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4">
                {/* Flat Fee Per Video */}
                {(contractDetails.flatFee?.perVideo ||
                  contract.contractTerms.flatFeePerVideo > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        Flat Fee Per Video
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(
                          contractDetails.flatFee?.perVideo ||
                          contract.contractTerms.flatFeePerVideo,
                        )}
                      </p>
                    </div>
                  )}

                {/* Time Period Fee (Monthly/Recurring) */}
                {contractDetails.flatFee?.timePeriod && (
                  <div>
                    <p className="text-sm text-primary/60">
                      {Math.abs(
                        (contractDetails.endDate - contractDetails.startDate) /
                        (1000 * 60 * 60 * 24) -
                        contractDetails.flatFee.timePeriod.days,
                      ) <= 1
                        ? "One Time Payment"
                        : `Flat Fee per ${contractDetails.flatFee.timePeriod.days} days`}
                    </p>
                    <p className="text-sm font-medium text-primary">
                      {formatCurrency(
                        contractDetails.flatFee.timePeriod.amount,
                      )}
                    </p>
                  </div>
                )}

                {/* CPM Rate */}
                {(contractDetails.cpmRate ||
                  contract.contractTerms.cpm > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        CPM (Cost Per Thousand Views)
                        {contractDetails.cpmRate?.type === "totalViews" &&
                          " - Total Views"}
                        {contractDetails.cpmRate?.type === "perVideo" &&
                          " - Per Video"}
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(
                          contractDetails.cpmRate?.rate ||
                          contract.contractTerms.cpm,
                          3,
                        )}
                      </p>
                    </div>
                  )}

                {/* Minimum Views Threshold */}
                {(contractDetails.cpmRate?.threshold ||
                  contract.contractTerms.minimumViewsRequiredPerVideo > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        Minimum Views Required
                        {contractDetails.cpmRate?.type === "totalViews" &&
                          " (Total)"}
                        {contractDetails.cpmRate?.type === "perVideo" &&
                          " (Per Video)"}
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {(
                          contractDetails.cpmRate?.threshold ||
                          contract.contractTerms.minimumViewsRequiredPerVideo
                        ).toLocaleString()}{" "}
                        views
                      </p>
                    </div>
                  )}

                {/* Maximum Payment Cap */}
                {(contractDetails.cpmRate?.maxPayout ||
                  contract.contractTerms.maximumPaymentCapPerVideo > 0) && (
                    <div>
                      <p className="text-sm text-primary/60">
                        Maximum Payment Cap
                        {contractDetails.cpmRate?.type === "totalViews" &&
                          " (Total)"}
                        {contractDetails.cpmRate?.type === "perVideo" &&
                          " (Per Video)"}
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(
                          contractDetails.cpmRate?.maxPayout ||
                          contract.contractTerms.maximumPaymentCapPerVideo,
                        )}
                      </p>
                    </div>
                  )}

                {/* CPM Cutoff Period */}
                {contractDetails.cpmRate?.cutoffPeriod && (
                  <div>
                    <p className="text-sm text-primary/60">
                      CPM Payment Cutoff Period
                    </p>
                    <p className="text-sm font-medium text-primary">
                      {contractDetails.cpmRate.cutoffPeriod} days after contract
                      ends
                    </p>
                  </div>
                )}

                {/* Required Videos */}
                <div>
                  <p className="text-sm text-primary/60">Required Videos</p>
                  <p className="text-sm font-medium text-primary">
                    {contractDetails.requiredVideos.count} video
                    {contractDetails.requiredVideos.count !== 1 ? "s" : ""}
                    {contractDetails.requiredVideos.duration &&
                      Math.abs(
                        (contractDetails.endDate - contractDetails.startDate) /
                        (1000 * 60 * 60 * 24) -
                        contractDetails.requiredVideos.duration,
                      ) <= 1
                      ? " for the duration of the contract"
                      : ` every ${contractDetails.requiredVideos.duration} days`}
                  </p>
                  <p className="text-xs text-primary/60 italic mt-1">
                    Note: This is the total number of videos you need to publish
                    across all platforms.
                  </p>
                </div>

                {/* Contract Duration */}
                <div>
                  <p className="text-sm text-primary/60">Contract Duration</p>
                  <p className="text-sm font-medium text-primary">
                    {formatDate(
                      new Date(contractDetails.startDate).toISOString(),
                    )}{" "}
                    -{" "}
                    {formatDate(
                      new Date(contractDetails.endDate).toISOString(),
                    )}
                  </p>
                </div>
              </div>
            </div>
            {contract.deliverables &&
              contractDetails.requiredVideos.count > 0 && (
                <div className="p-6">
                  <h2 className="text-lg font-medium text-primary">
                    Previous Deliverables
                  </h2>
                  <div className="mt-4 space-y-4">
                    <div className="rounded-lg bg-secondary p-4">
                      <p className="font-medium text-primary">
                        {contractDetails.requiredVideos.count} video
                        {contractDetails.requiredVideos.count !== 1 ? "s" : ""}
                        {contractDetails.requiredVideos.duration &&
                          Math.abs(
                            (contractDetails.endDate -
                              contractDetails.startDate) /
                            (1000 * 60 * 60 * 24) -
                            contractDetails.requiredVideos.duration,
                          ) <= 1
                          ? " for the duration of the contract"
                          : ` every ${contractDetails.requiredVideos.duration} days`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </Card>
        </div>
      );
    }

    // For active contracts, show the regular content
    return (
      <Card className="col-span-2 divide-y divide-border">
        <div className="p-6">
          <h2 className="text-lg font-medium text-primary">Contract Terms</h2>
          <div className="mt-4 grid grid-cols-1 gap-4">
            {/* Flat Fee Per Video */}
            {(contractDetails.flatFee?.perVideo ||
              contract.contractTerms.flatFeePerVideo > 0) && (
                <div>
                  <p className="text-sm text-primary/60">Flat Fee Per Video</p>
                  <p className="text-sm font-medium text-primary">
                    {formatCurrency(
                      contractDetails.flatFee?.perVideo ||
                      contract.contractTerms.flatFeePerVideo,
                    )}
                  </p>
                </div>
              )}

            {/* Time Period Fee (Monthly/Recurring) */}
            {contractDetails.flatFee?.timePeriod && (
              <div>
                <p className="text-sm text-primary/60">
                  {Math.abs(
                    (contractDetails.endDate - contractDetails.startDate) /
                    (1000 * 60 * 60 * 24) -
                    contractDetails.flatFee.timePeriod.days,
                  ) <= 1
                    ? "One Time Payment"
                    : `Flat Fee per ${contractDetails.flatFee.timePeriod.days} days`}
                </p>
                <p className="text-sm font-medium text-primary">
                  {formatCurrency(contractDetails.flatFee.timePeriod.amount)}
                </p>
              </div>
            )}

            {/* CPM Rate */}
            {(contractDetails.cpmRate || contract.contractTerms.cpm > 0) && (
              <div>
                <p className="text-sm text-primary/60">
                  CPM (Cost Per Thousand Views)
                  {contractDetails.cpmRate?.type === "totalViews" &&
                    " - Total Views"}
                  {contractDetails.cpmRate?.type === "perVideo" &&
                    " - Per Video"}
                </p>
                <p className="text-sm font-medium text-primary">
                  {formatCurrency(
                    contractDetails.cpmRate?.rate || contract.contractTerms.cpm,
                    3,
                  )}
                </p>
              </div>
            )}

            {/* Minimum Views Threshold */}
            {(contractDetails.cpmRate?.threshold ||
              contract.contractTerms.minimumViewsRequiredPerVideo > 0) && (
                <div>
                  <p className="text-sm text-primary/60">
                    Minimum Views Required
                    {contractDetails.cpmRate?.type === "totalViews" && " (Total)"}
                    {contractDetails.cpmRate?.type === "perVideo" &&
                      " (Per Video)"}
                  </p>
                  <p className="text-sm font-medium text-primary">
                    {(
                      contractDetails.cpmRate?.threshold ||
                      contract.contractTerms.minimumViewsRequiredPerVideo
                    ).toLocaleString()}{" "}
                    views
                  </p>
                </div>
              )}

            {/* Maximum Payment Cap */}
            {(contractDetails.cpmRate?.maxPayout ||
              contract.contractTerms.maximumPaymentCapPerVideo > 0) && (
                <div>
                  <p className="text-sm text-primary/60">
                    Maximum Payment Cap
                    {contractDetails.cpmRate?.type === "totalViews" && " (Total)"}
                    {contractDetails.cpmRate?.type === "perVideo" &&
                      " (Per Video)"}
                  </p>
                  <p className="text-sm font-medium text-primary">
                    {formatCurrency(
                      contractDetails.cpmRate?.maxPayout ||
                      contract.contractTerms.maximumPaymentCapPerVideo,
                    )}
                  </p>
                </div>
              )}

            {/* CPM Cutoff Period */}
            {contractDetails.cpmRate?.cutoffPeriod && (
              <div>
                <p className="text-sm text-primary/60">
                  CPM Payment Cutoff Period
                </p>
                <p className="text-sm font-medium text-primary">
                  {contractDetails.cpmRate.cutoffPeriod} days after contract
                  ends
                </p>
              </div>
            )}

            {/* Required Videos */}
            <div>
              <p className="text-sm text-primary/60">Required Videos</p>
              <p className="text-sm font-medium text-primary">
                {contractDetails.requiredVideos.count} video
                {contractDetails.requiredVideos.count !== 1 ? "s" : ""}
                {contractDetails.requiredVideos.duration &&
                  Math.abs(
                    (contractDetails.endDate - contractDetails.startDate) /
                    (1000 * 60 * 60 * 24) -
                    contractDetails.requiredVideos.duration,
                  ) <= 1
                  ? " for the duration of the contract"
                  : ` every ${contractDetails.requiredVideos.duration} days`}
              </p>
              <p className="text-xs text-primary/60 italic mt-1">
                Note: This is the total number of videos you need to publish
                across all platforms.
              </p>
            </div>

            {/* Contract Duration */}
            <div>
              <p className="text-sm text-primary/60">Contract Duration</p>
              <p className="text-sm font-medium text-primary">
                {formatDate(new Date(contractDetails.startDate).toISOString())}{" "}
                - {formatDate(new Date(contractDetails.endDate).toISOString())}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <PayoutCalculator
              flatFeePerVideo={
                contractDetails.flatFee?.perVideo ||
                contract.contractTerms.flatFeePerVideo
              }
              cpm={contractDetails.cpmRate?.rate || contract.contractTerms.cpm}
              maximumPaymentCap={
                contractDetails.cpmRate?.maxPayout ||
                contract.contractTerms.maximumPaymentCapPerVideo
              }
              flatFeeTimePeriod={contractDetails.flatFee?.timePeriod}
              cpmRateType={contractDetails.cpmRate?.type || "perVideo"}
              contractDurationDays={Math.ceil(
                Math.abs(
                  (contractDetails.endDate - contractDetails.startDate) /
                  (1000 * 60 * 60 * 24),
                ),
              )}
              requiredVideos={contractDetails.requiredVideos}
            />
          </div>
        </div>

        {contract.deliverables && contractDetails.requiredVideos.count > 0 && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-primary">Deliverables</h2>
            <div className="mt-4 space-y-4">
              <div className="rounded-lg bg-secondary p-4">
                <p className="font-medium text-primary">
                  {contractDetails.requiredVideos.count} video
                  {contractDetails.requiredVideos.count !== 1 ? "s" : ""}
                  {contractDetails.requiredVideos.duration &&
                    Math.abs(
                      (contractDetails.endDate - contractDetails.startDate) /
                      (1000 * 60 * 60 * 24) -
                      contractDetails.requiredVideos.duration,
                    ) <= 1
                    ? " for the duration of the contract"
                    : ` every ${contractDetails.requiredVideos.duration} days`}
                </p>
              </div>
            </div>
          </div>
        )}

        {contract.timeline && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-primary">
              Contract Timeline
            </h2>
            <div className="mt-4 space-y-4">
              {[
                ...contract.timeline,
                ...(contractRevisions?.map((revision) => ({
                  date: new Date(revision._creationTime).toISOString(),
                  event: `Contract Extension Requested (New End Date: ${revision.newEndDate ? formatDate(new Date(revision.newEndDate).toISOString()) : "Unknown"})`,
                  status:
                    revision.status === "approved"
                      ? ("completed" as const)
                      : revision.status === "rejected"
                        ? ("rejected" as const)
                        : ("pending" as const),
                })) || []),
              ]
                .sort(
                  (a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime(),
                )
                .map((event, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 rounded-lg bg-secondary p-4"
                  >
                    <CalendarIcon className="h-5 w-5 text-primary/60" />
                    <div>
                      <p className="font-medium text-primary">{event.event}</p>
                      <p className="text-sm text-primary/60">
                        {formatDate(event.date)}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "ml-auto rounded-full px-3 py-1 text-xs",
                        event.status === "completed"
                          ? "bg-green-500/10 text-green-500"
                          : event.status === "rejected"
                            ? "bg-red-500/10 text-red-500"
                            : event.status === "upcoming"
                              ? "bg-blue-500/10 text-blue-500"
                              : event.status === "pending"
                                ? "bg-yellow-500/10 text-yellow-500"
                                : "bg-primary/10 text-primary",
                      )}
                    >
                      {event.status.charAt(0).toUpperCase() +
                        event.status.slice(1)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (window.history.length >= 1) {
                  console.log("window.history.length", window.history.length);
                  window.history.back();
                } else {
                  navigate({
                    to: PartnershipsRoute.fullPath,
                  });
                }
              }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                <img
                  className="h-8 w-8 rounded-full object-cover"
                  alt={partnership?.app?.name}
                  src={partnership?.app?.logo}
                />
                <h1 className="text-2xl font-semibold text-primary">
                  {partnership?.campaign?.name} Partnership
                </h1>
                <p className="text-sm text-primary/60"></p>
              </div>
            </div>
          </div>
          <StatusBadge status={contract.status} />
        </div>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full mt-4"
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {contract.status !== "pendingApproval" &&
              contract.campaign.type !== "challenge" && (
                <TabsTrigger value="contract">Contract</TabsTrigger>
              )}
            {contract.status !== "pendingApproval" && (
              <TabsTrigger value="performance">Performance</TabsTrigger>
            )}
            {contract.status !== "pendingApproval" && (
              <TabsTrigger value="videos">Videos</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab
              contract={contract}
              partnership={partnership}
              contractRevisions={contractRevisions}
              paymentInfo={paymentInfo}
              user={user}
              appId={appId}
              creatorId={creatorId}
              renderMainContent={renderMainContent}
              setShowDocumentUpload={setShowDocumentUpload}
              setShowExtendContractDialog={setShowExtendContractDialog}
              setShowInvoiceUploadDialog={setShowInvoiceUploadDialog}
              handleRemoveDocument={handleRemoveDocument}
              handleApproveReject={handleApproveReject}
              terminatePartnership={terminatePartnership}
              toast={toast}
              isApprovingRejecting={isApprovingRejecting}
              formatRevisionDate={formatRevisionDate}
            />
          </TabsContent>

          <TabsContent value="contract">
            <ContractTab contract={partnership.partnership} />
          </TabsContent>

          {contract.status !== "pendingApproval" && (
            <TabsContent value="performance">
              <PartnershipPerformance
                contractId={partnershipId as Id<"creatorContracts">}
              />
            </TabsContent>
          )}

          {contract.status !== "pendingApproval" && (
            <TabsContent value="videos">
              <PartnershipFeedbackVideos
                contractId={partnershipId as Id<"creatorContracts">}
                initialProjectId={search.projectId}
                initialVersionId={search.versionId}
                onNavigate={handleVideoNavigation}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* Add FloatingChat component
        {partnership.campaign._id && creator && (
          <FloatingChat
            creator={creator}
            campaignId={partnership.campaign._id}
            isChatOpen={isChatOpen}
            setIsChatOpen={setIsChatOpen}
          />
        )} */}
      </div>

      <PartnershipDocumentUpload
        open={showDocumentUpload}
        onOpenChange={setShowDocumentUpload}
        partnershipId={partnershipId as Id<"creatorContracts">}
      />

      <PartnershipTermsDialog
        open={showContractForm}
        onOpenChange={setShowContractForm}
        contractDetails={contractDetails}
        onContractDetailsChange={(newDetails: ContractDetails) => {
          setContractDetails(newDetails);
        }}
        onSubmit={async () => {
          try {
            await createUpdatePartnership({
              partnershipId: partnershipId as Id<"creatorContracts">,
              campaignId: partnership.campaign._id,
              startDate: contractDetails.startDate,
              endDate: contractDetails.endDate,
              currency: contractDetails.currency,
              requiredVideos: contractDetails.requiredVideos,
              flatFee: contractDetails.flatFee,
              cpmRate: contractDetails.cpmRate
                ? {
                  ...contractDetails.cpmRate,
                  cutoffPeriod: contractDetails.cpmRate.cutoffPeriod,
                }
                : undefined,
            });
            setShowContractForm(false);
            toast({
              title: "Contract updated",
              description: "The contract has been updated successfully",
            });
          } catch (error) {
            console.error(error);
            toast({
              title: "Error updating contract",
              description:
                error instanceof ConvexError
                  ? error.data
                  : ERRORS.SOMETHING_WENT_WRONG,
            });
          }
        }}
        isSubmitting={isCreatingPartnership}
        title="Edit Contract Terms"
        description="Update the terms for the partnership contract. The creator will need to approve these changes."
        submitButtonText="Update Terms"
        maxEndDate={partnership?.campaign?.endDate}
      />

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Partnership</DialogTitle>
            <DialogDescription>
              You are about to enter into a partnership agreement with{" "}
              {partnership?.app?.name}. This will create a binding contract
              between you and the app. Please make sure you have reviewed all
              terms and conditions before proceeding.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAcceptTerms}>Confirm & Accept Terms</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add the Extend Contract Dialog */}
      <Dialog
        open={showExtendContractDialog}
        onOpenChange={setShowExtendContractDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Contract</DialogTitle>
            <DialogDescription>
              Select a new end date for the contract. The creator will need to
              approve this extension.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2 ">
              <label className="text-sm font-medium text-primary">
                New End Date
              </label>
              <Calendar
                mode="single"
                selected={newEndDate}
                onSelect={setNewEndDate}
                disabled={(date) => {
                  if (date < new Date()) {
                    return true;
                  }
                  if (
                    contractDetails.endDate &&
                    date < new Date(contractDetails.endDate)
                  ) {
                    return true;
                  }
                  return false;
                }}
                initialFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowExtendContractDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExtendContract}
              disabled={!newEndDate || isCreatingRevision}
            >
              {isCreatingRevision ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Requesting...
                </>
              ) : (
                "Request Extension"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add the Invoice Upload Dialog */}
      <Dialog open={showInvoiceUploadDialog} onOpenChange={closeInvoiceModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Upload Invoice</DialogTitle>
            <DialogDescription>
              Upload your invoice with payment details so the company can
              process your payment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 px-1 scrollbar-hide">
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">
                  Total Earnings:
                </span>
                <span className="text-sm font-medium">
                  {paymentInfo && formatCurrency(paymentInfo.totalEarnings)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Please include your payment details (bank account, card, email,
                etc.) in your invoice.
              </p>
            </div>

            {/* Invoice Mode Toggle */}
            <div className="space-y-2">
              <Label>Invoice</Label>
              <div className="flex rounded-lg border border-input p-1">
                <button
                  type="button"
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${invoiceMode === "file"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                    }`}
                  onClick={() => {
                    setInvoiceMode("file");
                    setUrlError("");
                  }}
                >
                  <Upload className="h-4 w-4 mr-2 inline" />
                  Upload File
                </button>
                <button
                  type="button"
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${invoiceMode === "url"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                    }`}
                  onClick={() => {
                    setInvoiceMode("url");
                    setUrlError("");
                  }}
                >
                  <LinkIcon className="h-4 w-4 mr-2 inline" />
                  Invoice URL
                </button>
              </div>
            </div>

            {invoiceMode === "file" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) =>
                      setInvoiceFile(e.target.files?.[0] || null)
                    }
                    className="flex-1"
                  />
                  {invoiceFile && (
                    <div className="flex items-center gap-1 text-sm text-green-600">
                      <Upload className="h-4 w-4" />
                      {invoiceFile.name}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Upload an image or PDF of your invoice with payment details
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div
                  className={`flex items-center gap-2 rounded-md border bg-background px-3 py-2 ${urlError ? "border-red-500" : "border-input"
                    }`}
                >
                  <LinkIcon className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="url"
                    value={invoiceUrl}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="https://example.com/invoice.pdf"
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                {urlError && <p className="text-xs text-red-500">{urlError}</p>}
                <p className="text-xs text-muted-foreground">
                  Provide a URL to your invoice (must start with https://)
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 flex-shrink-0 mt-4">
            <Button variant="outline" onClick={closeInvoiceModal}>
              Cancel
            </Button>
            <Button
              onClick={handleInvoiceUpload}
              disabled={
                isUploadingInvoice ||
                (invoiceMode === "file" && !invoiceFile) ||
                (invoiceMode === "url" && (!invoiceUrl.trim() || !!urlError))
              }
            >
              {isUploadingInvoice ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {invoiceMode === "file" ? "Uploading..." : "Uploading..."}
                </>
              ) : (
                "Upload Invoice"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
